import React from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useServiceList } from "@/contexts/ServiceListContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Minus, Plus, Trash2, Calendar as CalendarIcon, Clock, User, Mail, Phone, X, ArrowRight } from "lucide-react";
import {
    trackSpaTrayClose,
    trackSpaTrayViewNavigation,
    trackSpaTrayQuantityChange,
    trackSpaTrayItemRemove,
    trackSpaTrayFormStart,
    trackSpaTrayFormSubmit,
} from "@/utils/analytics";
import { LotusIcon } from "./LotusIcon";
import { IconMassage } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { translations } from "@/data/translations";
import { useNavigate, useLocation } from "react-router-dom";
import { spaServices as services, saunaFacilities } from "@/data/spaServices";
import { extractDurationPricePairs, getSingleLanguageTitle } from "@/utils/spaUtils";
import { toast } from "sonner";
import { format } from "date-fns";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getSpaBlurPlaceholder } from "@/utils/spaBlurPlaceholders";

// Helper function to get the correct image URL for a service
const getServiceImageUrl = (item: { serviceId: string; category: string }): string => {
    // For massages, find the service in spaServices
    if (item.category === 'massage') {
        const service = services.find(s => s.serviceId === item.serviceId);
        if (service) {
            return service.imageUrl;
        }
    }
    
    // For pelvipower
    if (item.category === 'pelvipower') {
        return "/images/Clinique Nutraswiss/pelvipower.desktop.webp";
    }
    
    // For sauna, use Bio Sauna image
    if (item.category === 'sauna') {
        // Bio Sauna is the second item in saunaFacilities array (index 1)
        const bioSauna = saunaFacilities.find(f => f.name.includes('bio') || f.name.includes('Bio'));
        if (bioSauna) {
            return bioSauna.image;
        }
        // Fallback to Bio Sauna path (direct reference)
        return "/images/Clinique Nutraswiss/Sauna package images/Bio Sauna.jpg";
    }
    
    // Fallback
    return "/images/wellness.jpg";
};

const ServiceCatalogItem = ({ service, currentView }: { service: typeof services[0]; currentView: 'selection' | 'catalog' }) => {
    const { items, addItem, updateQuantity } = useServiceList();
    const { language } = useLanguage();
    const t = (translations[language] as any)?.spa?.serviceList || translations.en.spa.serviceList;

    // Logic from the map loop
    let serviceData: any;
    if (service.category === 'massage') {
        serviceData = (translations[language] as any)?.spa?.treatmentsAndServices?.therapeuticMassages?.[service.translationKey.split('.').pop() || ''];
    } else if (service.category === 'sauna') {
        serviceData = (translations[language] as any)?.spa?.treatmentsAndServices?.saunaThermal?.package;
    } else if (service.category === 'pelvipower') {
        serviceData = (translations[language] as any)?.spa?.treatmentsAndServices?.specialPrograms?.pelvipower;
    }

    const serviceName = getSingleLanguageTitle(serviceData?.name || serviceData?.title || service.translationKey, service.translationKey, language);
    const pricingStr = serviceData?.pricing || serviceData?.price || '';
    let durationPairs = extractDurationPricePairs(pricingStr);

    // Fallback for single-price items (Sauna, PelviPower)
    if (durationPairs.length === 0 && pricingStr) {
        const priceMatch = pricingStr.match(/(\d+)/);
        const durationMatch = pricingStr.match(/(\d+)\s*Min/);
        if (priceMatch) {
            durationPairs = [{
                duration: durationMatch ? durationMatch[1] : (service.category === 'sauna' ? 'Session' : '60'),
                price: priceMatch[1]
            }];
        }
    }

    // For single-price items (Sauna, PelviPower), use serviceId as the ID (not serviceId-duration)
    // This ensures they aggregate properly with items added from page selectors
    const getItemId = (serviceId: string, duration: string, category: string) => {
        if (category === 'sauna' || category === 'pelvipower') {
            return serviceId; // Use just serviceId for single-price items
        }
        return `${serviceId}-${duration}`; // Use serviceId-duration for multi-price items
    };

    return (
        <div className="space-y-3 pb-6 border-b border-white/5 last:border-0 last:pb-2 group">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-alpine-gold/10 flex items-center justify-center border border-alpine-gold/20 group-hover:border-alpine-gold/50 transition-colors">
                    <IconMassage className="w-5 h-5 text-alpine-gold" />
                </div>

                <div>
                    <h4 className="text-white font-playfair text-lg leading-tight">{serviceName}</h4>
                    {service.category === 'sauna' && (
                        <p className="text-white/40 text-[9px] uppercase tracking-widest font-bold mt-1">
                            {serviceData?.hours || '11:00 - 17:00 (may extend until 20:00)'}
                        </p>
                    )}
                </div>
            </div>

            <p className="text-white/50 text-xs font-lato leading-relaxed px-1">
                {serviceData?.description || ''}
            </p>



            <div className="space-y-1.5 pt-1">
                {durationPairs.map((pair) => {
                    const itemId = getItemId(service.serviceId, pair.duration, service.category);
                    const existingItem = items.find(i => i.id === itemId);

                    return (
                        <div
                            key={itemId}
                            className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-2.5 px-4 transition-all duration-300 group/item"
                        >
                            <div className="flex items-baseline gap-3">
                                <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold font-lato">
                                    {pair.duration === 'Session' ? 'Package' : `${pair.duration} Min`}
                                </span>
                                <span className="text-alpine-gold font-bold font-playfair text-base">CHF {pair.price}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                {existingItem ? (
                                    <div className="flex items-center bg-black/40 rounded-full border border-white/10 p-1">
                                        <button
                                            onClick={() => {
                                                const previousQty = existingItem.quantity;
                                                updateQuantity(itemId, -1);
                                                trackSpaTrayQuantityChange('decrement', itemId, serviceName, previousQty, previousQty - 1, currentView);
                                            }}
                                            className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                                        >
                                            <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        <span className="w-6 text-center text-[11px] font-bold font-lato text-white">{existingItem.quantity}</span>
                                        <button
                                            onClick={() => {
                                                const previousQty = existingItem.quantity;
                                                updateQuantity(itemId, 1);
                                                trackSpaTrayQuantityChange('increment', itemId, serviceName, previousQty, previousQty + 1, currentView);
                                            }}
                                            className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => {
                                            // Track the 0→1 change before adding
                                            trackSpaTrayQuantityChange('increment', itemId, serviceName, 0, 1, currentView);
                                            
                                            addItem({
                                                id: itemId,
                                                serviceId: service.serviceId,
                                                name: serviceName,
                                                category: service.category,
                                                price: parseInt(pair.price),
                                                duration: pair.duration === 'Session' ? 'Package' : `${pair.duration} Min`,
                                                imageUrl: service.imageUrl
                                            });
                                            toast.success(t.addedToList || 'Added to selections', {
                                                duration: 2000,
                                                position: 'top-center',
                                            });
                                        }}
                                        className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:border-alpine-gold hover:text-alpine-gold transition-colors bg-black/40"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div >
    );
};

export const ServiceListTray: React.FC = () => {
    const { items, addItem, removeItem, updateQuantity, totalPrice, isTrayOpen, setIsTrayOpen, clearList, totalItems, traySource } = useServiceList();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [isHovered, setIsHovered] = React.useState(false);
    const [view, setView] = React.useState<'selection' | 'catalog'>('selection');
    const [trayOpenTime, setTrayOpenTime] = React.useState<number | null>(null);
    const formStartedRef = React.useRef(false);

    const t = (translations[language] as any)?.spa?.serviceList || translations.en.spa.serviceList;

    const [formData, setFormData] = React.useState({
        fullName: "",
        email: "",
        phone: "",
        preferredDate: "" as string | Date,
        preferredTime: "",
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'success' | 'error'>('idle');

    // Reset hover state when tray opens/closes to prevent "sticky" labels
    React.useEffect(() => {
        setIsHovered(false);
    }, [isTrayOpen]);

    // Track tray open time
    React.useEffect(() => {
        if (isTrayOpen) {
            setTrayOpenTime(Date.now());
            formStartedRef.current = false;
        }
    }, [isTrayOpen]);

    // Handle tray close with tracking
    const handleTrayClose = (open: boolean) => {
        if (!open && trayOpenTime) {
            const timeSpentSeconds = Math.floor((Date.now() - trayOpenTime) / 1000);
            trackSpaTrayClose(
                traySource,
                timeSpentSeconds,
                view,
                formStartedRef.current,
                totalItems,
                totalPrice
            );
            setTrayOpenTime(null);
            formStartedRef.current = false;
        }
        setIsTrayOpen(open);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDateSelect = (date: Date | undefined) => {
        if (date) {
            // Create date string in local timezone to avoid timezone shift
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            setFormData((prev) => ({
                ...prev,
                preferredDate: dateStr,
            }));
        }
    };

    // Helper to get today's date at midnight for comparison
    const getTodayAtMidnight = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate all required fields
        if (!formData.fullName || !formData.email || !formData.phone || !formData.preferredDate || !formData.preferredTime) {
            toast.error("Please fill in all required fields", {
                duration: 2500,
                position: 'top-center',
            });
            return;
        }
        
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            // Ensure preferredDate is a string in YYYY-MM-DD format
            const dateStr = typeof formData.preferredDate === 'string' 
                ? formData.preferredDate 
                : (formData.preferredDate as Date).toISOString().split('T')[0];
            
            // Prepare API payload according to backend schema
            const payload = {
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                preferredDate: dateStr,
                preferredTime: formData.preferredTime, // Already "Morning" | "Afternoon" | "Evening"
                items: items.map(item => ({
                    id: item.id,
                    name: item.name,
                    category: item.category,
                    price: item.price,
                    duration: item.duration,
                    quantity: item.quantity,
                })),
                totalPrice: totalPrice,
                language: language,
                timestamp: new Date().toISOString(),
            };

            // Call API endpoint (proxied to backend in development)
            const response = await fetch("/api/spa-inquiry", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                // Handle API errors
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData.error || "Failed to send inquiry. Please try again.";
                
                setSubmitStatus('error');
                trackSpaTrayFormSubmit(totalItems, totalPrice, 'error');
                toast.error(errorMsg, {
                    duration: 3000,
                    position: 'top-center',
                });
                setIsSubmitting(false);
                return;
            }

            // Success - clear list and form
            const result = await response.json();
            setSubmitStatus('success');
            trackSpaTrayFormSubmit(totalItems, totalPrice, 'success');
            clearList();
            toast.success(t.successMessage || "Inquiry sent successfully!", {
                duration: 2000,
                position: 'top-center',
            });
            
            // Close tray after showing success message
            setTimeout(() => {
                setIsTrayOpen(false);
                setSubmitStatus('idle');
                setFormData({
                    fullName: "",
                    email: "",
                    phone: "",
                    preferredDate: "",
                    preferredTime: "",
                });
                setView('selection');
            }, 2000);

        } catch (error) {
            console.error("Spa inquiry submission error:", error);
            setSubmitStatus('error');
            trackSpaTrayFormSubmit(totalItems, totalPrice, 'error');
            toast.error("Network error. Please check your connection and try again.", {
                duration: 3000,
                position: 'top-center',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <AnimatePresence>
                {!isTrayOpen && (
                    <motion.button
                        initial={{ x: 100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 100, opacity: 0 }}
                        onHoverStart={() => setIsHovered(true)}
                        onHoverEnd={() => setIsHovered(false)}
                        onClick={() => setIsTrayOpen(true, 'tray_button')}
                        className="fixed right-0 top-1/3 z-50 bg-alpine-gold text-black rounded-l-xl shadow-[0_4px_20px_rgba(0,0,0,0.3)] flex items-center justify-start transition-all duration-300 border-y border-l border-white/20 border-r-0"
                    >
                        <div className="flex items-center gap-3 pl-4 pr-3 py-3">
                            {/* Text - Reveals on Hover */}
                            <motion.span
                                initial={{ width: 0, opacity: 0, marginRight: 0 }}
                                animate={{
                                    width: isHovered ? "auto" : 0,
                                    opacity: isHovered ? 1 : 0,
                                    marginRight: isHovered ? 8 : 0
                                }}
                                className="font-playfair text-xs font-bold uppercase tracking-widest whitespace-nowrap overflow-hidden"
                            >
                                {t.title || "Your Service List"}
                            </motion.span>

                            {/* Icon & Badge - Always Visible */}
                            <div className="relative shrink-0">
                                <IconMassage className="w-6 h-6" />
                                {totalItems > 0 && (
                                    <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-black text-white text-[10px] font-bold flex items-center justify-center shadow-md">
                                        {totalItems}
                                    </span>
                                )}
                            </div>
                        </div>
                    </motion.button>
                )}
            </AnimatePresence>

            <Sheet open={isTrayOpen} onOpenChange={handleTrayClose}>
                <SheetContent side="right" className="w-full sm:w-[500px] border-l border-white/10 bg-gray-900/95 backdrop-blur-xl p-0 shadow-2xl z-[100]">
                    <div className="h-full flex flex-col relative overflow-hidden">
                        {/* Background Texture - Lighter */}
                        <div className="absolute inset-0 bg-[#1a1d24] opacity-80 z-[-1]" />
                        <div className="absolute inset-0 bg-[url('/images/texture-noise.png')] opacity-[0.02] z-[-1] pointer-events-none" />

                        <SheetHeader className="p-6 border-b border-white/10 relative">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <IconMassage className="w-6 h-6 text-alpine-gold" />
                                    <SheetTitle className="text-white font-playfair text-xl tracking-wide uppercase">
                                        {t.title || "Your Service List"}
                                    </SheetTitle>
                                </div>
                                <button
                                    onClick={() => handleTrayClose(false)}
                                    className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                                >
                                    <X className="w-4 h-4 text-white/70" />
                                </button>
                            </div>

                            {/* View Switcher - Only show if not empty? Or always */}
                            <div className="flex bg-white/5 rounded-lg p-1 mt-6 border border-white/5 relative">
                                <div
                                    className="absolute top-1 bottom-1 bg-alpine-gold/10 rounded-md border border-alpine-gold/20 transition-all duration-300 ease-out"
                                    style={{
                                        left: view === 'selection' ? '4px' : '50%',
                                        width: 'calc(50% - 4px)',
                                        transform: view === 'catalog' ? 'translateX(0)' : 'translateX(0)'
                                    }}
                                />
                                <button
                                    onClick={() => {
                                        if (view !== 'selection') {
                                            trackSpaTrayViewNavigation(view, 'selection', totalItems);
                                        }
                                        setView('selection');
                                    }}
                                    className={`flex-1 py-2 text-[10px] uppercase tracking-widest font-bold font-lato transition-colors relative z-10 ${view === 'selection' ? 'text-alpine-gold' : 'text-white/40 hover:text-white/60'}`}
                                >
                                    {t.selections || "Selections"}
                                </button>
                                <button
                                    onClick={() => {
                                        if (view !== 'catalog') {
                                            trackSpaTrayViewNavigation(view, 'catalog', totalItems);
                                        }
                                        setView('catalog');
                                    }}
                                    className={`flex-1 py-2 text-[10px] uppercase tracking-widest font-bold font-lato transition-colors relative z-10 ${view === 'catalog' ? 'text-alpine-gold' : 'text-white/40 hover:text-white/60'}`}
                                >
                                    {t.catalog || "Catalog"}
                                </button>
                            </div>
                        </SheetHeader>

                        <AnimatePresence mode="wait">
                            {view === 'selection' ? (
                                <motion.div
                                    key="selection-view"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="flex-1 flex flex-col min-h-0 overflow-hidden"
                                >
                                    {items.length === 0 && submitStatus === 'idle' ? (
                                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-white/40">
                                            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-6">
                                                <IconMassage className="w-8 h-8 opacity-50" />
                                            </div>
                                            <p className="font-playfair text-lg italic mb-2">{t.yourRelaxationAwaits || "Your relaxation awaits"}</p>
                                            <p className="text-xs font-lato max-w-[200px] leading-relaxed mb-8">
                                                {t.selectTreatmentsMessage || "Select treatments from our menu to create your personalized wellness plan."}
                                            </p>
                                            <button
                                                onClick={() => {
                                                    trackSpaTrayViewNavigation('selection', 'catalog', totalItems);
                                                    setView('catalog');
                                                }}
                                                className="px-6 py-3 bg-white/5 hover:bg-white/10 text-alpine-gold uppercase tracking-widest text-[10px] font-bold rounded-lg border border-alpine-gold/30 transition-all"
                                            >
                                                {t.viewSpaMenu || "View Spa Menu"}
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {/* List */}
                                            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
                                                {items.map((item) => (
                                                    <div key={item.id} className="relative group bg-white/5 border border-white/10 hover:border-alpine-gold/30 rounded-xl p-4 transition-all duration-300">
                                                        <div className="flex gap-4">
                                                            <div className="w-16 h-16 rounded-lg bg-black/40 overflow-hidden flex-shrink-0 relative">
                                                                <OptimizedImage
                                                                    src={getServiceImageUrl(item)}
                                                                    alt={item.name}
                                                                    className="w-full h-full object-cover opacity-80"
                                                                    blurDataURL={getSpaBlurPlaceholder(getServiceImageUrl(item))}
                                                                    fetchPriority="auto"
                                                                />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex justify-between items-start mb-1">
                                                                    <h4 className="text-white font-playfair text-sm leading-tight truncate pr-4">{item.name}</h4>
                                                                    <button
                                                                        onClick={() => {
                                                                            trackSpaTrayItemRemove(item.id, item.name, totalItems - 1);
                                                                            removeItem(item.id);
                                                                        }}
                                                                        className="text-white/30 hover:text-red-400 transition-colors p-1 -mr-2 -mt-2 opacity-0 group-hover:opacity-100"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                                <div className="flex items-center gap-2 mb-3">
                                                                    <span className="bg-white/10 text-white/60 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold">
                                                                        {item.duration}
                                                                    </span>
                                                                    <span className="text-alpine-gold font-bold text-xs">CHF {item.price * item.quantity}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <button
                                                                        onClick={() => {
                                                                            const previousQty = item.quantity;
                                                                            updateQuantity(item.id, -1);
                                                                            trackSpaTrayQuantityChange('decrement', item.id, item.name, previousQty, previousQty - 1, view);
                                                                        }}
                                                                        className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                                                                    >
                                                                        <Minus className="w-3 h-3 text-white/60" />
                                                                    </button>
                                                                    <span className="text-white text-xs font-bold w-4 text-center">{item.quantity}</span>
                                                                    <button
                                                                        onClick={() => {
                                                                            const previousQty = item.quantity;
                                                                            updateQuantity(item.id, 1);
                                                                            trackSpaTrayQuantityChange('increment', item.id, item.name, previousQty, previousQty + 1, view);
                                                                        }}
                                                                        className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                                                                    >
                                                                        <Plus className="w-3 h-3 text-white/60" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Footer */}
                                            <div className="p-6 border-t border-white/10 bg-black/60 backdrop-blur-md space-y-6">
                                                <div className="flex items-baseline justify-between">
                                                    <span className="text-white/60 text-xs font-lato uppercase tracking-widest">{t.totalEstimated || "Total Estimated"}</span>
                                                    <span className="text-2xl font-playfair text-alpine-gold font-bold">CHF {totalPrice}</span>
                                                </div>

                                                {submitStatus === 'success' ? (
                                                    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
                                                        <p className="text-green-400 font-playfair italic">{t.thankYouReceived || "Thank you! Your request has been received."}</p>
                                                    </div>
                                                ) : (
                                                    <form 
                                                        onSubmit={handleSubmit} 
                                                        onFocus={() => {
                                                            if (!formStartedRef.current) {
                                                                formStartedRef.current = true;
                                                                trackSpaTrayFormStart(totalItems, totalPrice);
                                                            }
                                                        }}
                                                        className="space-y-4"
                                                    >
                                                        <input
                                                            type="text"
                                                            name="fullName"
                                                            placeholder={`${t.name || "Full Name"} *`}
                                                            required
                                                            value={formData.fullName}
                                                            onChange={handleInputChange}
                                                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-alpine-gold/50 transition-colors"
                                                        />
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            placeholder={`${t.email || "Email"} *`}
                                                            required
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-alpine-gold/50 transition-colors"
                                                        />
                                                        <input
                                                            type="tel"
                                                            name="phone"
                                                            placeholder={`${t.phone || "Phone"} *`}
                                                            required
                                                            minLength={5}
                                                            value={formData.phone}
                                                            onChange={handleInputChange}
                                                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-alpine-gold/50 transition-colors"
                                                        />
                                                        <div className="space-y-2 pt-1">
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <div className="space-y-1.5">
                                                                    <label className="text-[9px] uppercase tracking-widest text-white/40 font-bold pl-1">{t.preferredDateCET || "Preferred Date (CET) *"}</label>
                                                                    <Popover>
                                                                        <PopoverTrigger asChild>
                                                                            <Button
                                                                                type="button"
                                                                                variant="outline"
                                                                                className={`w-full justify-start text-left font-normal bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white/80 hover:bg-white/10 focus:outline-none focus:border-alpine-gold/50 transition-colors ${!formData.preferredDate ? 'text-white/40' : ''}`}
                                                                            >
                                                                                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                                                                {formData.preferredDate 
                                                                                    ? format(new Date(formData.preferredDate as string), "MMM dd, yyyy")
                                                                                    : (t.selectDate || "Select Date")}
                                                                            </Button>
                                                                        </PopoverTrigger>
                                                                        <PopoverContent className="w-auto p-0 bg-black/95 border-white/10 z-[200]" align="start" sideOffset={4}>
                                                                            <Calendar
                                                                                mode="single"
                                                                                selected={formData.preferredDate ? (() => {
                                                                                    // Parse date string in local timezone to avoid timezone shift
                                                                                    const dateStr = formData.preferredDate as string;
                                                                                    const [year, month, day] = dateStr.split('-').map(Number);
                                                                                    return new Date(year, month - 1, day);
                                                                                })() : undefined}
                                                                                onSelect={handleDateSelect}
                                                                                disabled={(date) => {
                                                                                    const today = getTodayAtMidnight();
                                                                                    const dateToCheck = new Date(date);
                                                                                    dateToCheck.setHours(0, 0, 0, 0);
                                                                                    return dateToCheck < today;
                                                                                }}
                                                                                initialFocus
                                                                                className="bg-transparent text-white"
                                                                                classNames={{
                                                                                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                                                                    month: "space-y-4",
                                                                                    caption: "flex justify-center pt-1 relative items-center",
                                                                                    caption_label: "text-sm font-medium text-alpine-gold",
                                                                                    nav: "space-x-1 flex items-center",
                                                                                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white hover:text-alpine-gold border border-white/20 hover:border-alpine-gold/50 rounded-md",
                                                                                    nav_button_previous: "absolute left-1",
                                                                                    nav_button_next: "absolute right-1",
                                                                                    table: "w-full border-collapse space-y-1",
                                                                                    head_row: "flex",
                                                                                    head_cell: "text-white/60 rounded-md w-9 font-normal text-[0.8rem]",
                                                                                    row: "flex w-full mt-2",
                                                                                    cell: "h-9 w-9 text-center text-sm p-0 relative",
                                                                                    day: "h-9 w-9 p-0 font-normal text-white hover:bg-alpine-gold/20 hover:text-alpine-gold rounded-md transition-colors",
                                                                                    day_selected: "bg-alpine-gold text-black hover:bg-alpine-gold hover:text-black focus:bg-alpine-gold focus:text-black font-semibold",
                                                                                    day_today: "bg-white/10 text-alpine-gold border border-alpine-gold/50 font-semibold",
                                                                                    day_outside: "text-white/30 opacity-50",
                                                                                    day_disabled: "text-white/20 opacity-30 cursor-not-allowed",
                                                                                    day_range_middle: "bg-alpine-gold/20 text-alpine-gold",
                                                                                    day_hidden: "invisible",
                                                                                }}
                                                                            />
                                                                        </PopoverContent>
                                                                    </Popover>
                                                                    <input
                                                                        type="hidden"
                                                                        name="preferredDate"
                                                                        value={typeof formData.preferredDate === 'string' ? formData.preferredDate : (formData.preferredDate ? (formData.preferredDate as Date).toISOString().split('T')[0] : '')}
                                                                        required
                                                                    />
                                                                </div>
                                                                <div className="space-y-1.5">
                                                                    <label className="text-[9px] uppercase tracking-widest text-white/40 font-bold pl-1">{t.time || "Preferred Time"} *</label>
                                                                    <select
                                                                        name="preferredTime"
                                                                        value={formData.preferredTime}
                                                                        onChange={handleInputChange}
                                                                        required
                                                                        className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white/80 focus:outline-none focus:border-alpine-gold/50 transition-colors w-full"
                                                                    >
                                                                        <option value="">{t.selectTime || "Select Time"}</option>
                                                                        <option value="Morning">{t.morning || "Morning"}</option>
                                                                        <option value="Afternoon">{t.afternoon || "Afternoon"}</option>
                                                                        <option value="Evening">{t.evening || "Evening"}</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <p className="text-[10px] text-white/30 italic pl-1 leading-relaxed">
                                                                {t.requestedTimesNote || "*Requested times are subject to availability. We will contact you to confirm your appointment."}
                                                            </p>
                                                        </div>

                                                        <button
                                                            type="submit"
                                                            disabled={isSubmitting || items.length === 0}
                                                            className="w-full bg-alpine-gold text-black hover:scale-[1.02] active:scale-[0.98] transition-all py-3.5 rounded-xl font-extrabold uppercase tracking-[0.2em] text-[10px] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed group flex items-center justify-center gap-2"
                                                        >
                                                            {isSubmitting ? (
                                                                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                                            ) : (
                                                                <>
                                                                    {t.requestAppointment || "Request Appointment"}
                                                                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                                                </>
                                                            )}
                                                        </button>
                                                    </form>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="catalog-view"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className="flex-1 flex flex-col min-h-0 overflow-hidden"
                                >
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6 pt-2">
                                        {/* Group and render by category */}
                                        {Object.entries(
                                            services.reduce((acc, s) => {
                                                const cat = s.category || 'massage';
                                                if (!acc[cat]) acc[cat] = [];
                                                acc[cat].push(s);
                                                return acc;
                                            }, {} as Record<string, typeof services>)
                                        ).map(([category, catServices]) => {
                                            // Put Therapeutic Massages in accordion, show others directly
                                            if (category === 'massage') {
                                                return (
                                                    <Accordion key={category} type="single" collapsible className="w-full">
                                                        <AccordionItem value="therapeutic-massages" className="border-b border-alpine-gold/20">
                                                            <AccordionTrigger className="text-alpine-gold font-playfair text-base uppercase tracking-widest font-bold hover:no-underline py-3 text-left justify-between [&>svg]:text-alpine-gold [&>svg]:w-5 [&>svg]:h-5 [&>svg]:opacity-100 [&>svg]:ml-4">
                                                                {(translations[language] as any)?.spa?.treatmentsAndServices?.therapeuticMassages?.title || 'Therapeutic Massages'}
                                                            </AccordionTrigger>
                                                            <AccordionContent className="pt-4 space-y-4">
                                                                {catServices.map((service) => <ServiceCatalogItem key={service.serviceId} service={service} currentView={view} />)}
                                                            </AccordionContent>
                                                        </AccordionItem>
                                                    </Accordion>
                                                );
                                            }
                                            
                                            return (
                                                <div key={category} className="space-y-4">
                                                    <h3 className="text-alpine-gold font-playfair text-base uppercase tracking-widest font-bold border-b border-alpine-gold/20 pb-2 mb-4 text-left">
                                                        {category === 'sauna' && 'Wellness Packages'}
                                                        {category === 'pelvipower' && 'Specialized Programs'}
                                                    </h3>

                                                    {catServices.map((service) => <ServiceCatalogItem key={service.serviceId} service={service} currentView={view} />)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="p-6 border-t border-white/10 bg-black/60 backdrop-blur-md">
                                        <button
                                            onClick={() => {
                                                trackSpaTrayViewNavigation('catalog', 'selection', totalItems);
                                                setView('selection');
                                            }}
                                            className="w-full bg-alpine-gold text-black hover:scale-[1.02] active:scale-[0.98] transition-transform py-4 rounded-2xl font-extrabold uppercase tracking-[0.3em] text-[10px] shadow-2xl flex items-center justify-center gap-4"
                                        >
                                            {t.reviewSelections || "Review Selections"} ({totalItems})
                                            <ArrowRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
};
